/**
 * useShellNav — THE navigation model of the end-user shell.
 *
 * One model, seven skins: every layout under `shell/layouts/` is
 * presentational and reads this. It owns
 *   - the tree (built from the flat navigation by parent_id, any depth),
 *   - the current screen (URL-controlled or internal), the Home rule and the
 *     smart-page `navigate` hand-off,
 *   - Pinned and Recent (server record + local mirror, rights-filtered on
 *     every render: only screens in `nav` — which the host has already
 *     filtered by roles and grants — are ever shown),
 *   - Open editors (the session list; each session's form lives in its own
 *     kept-alive TxnScreen, see ShellFrame + editorContext),
 *   - full screen and the layout choice.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isLayoutId, layoutDefaults, resolveLayout, resolveShellConfig, type AppShellSettings, type LayoutId, type ResolvedShellConfig } from './config'
import {
  EMPTY_PREFS, movePinned, normalisePrefs, pushRecent, readMirror, togglePinned, writeMirror,
  visibleEntries, type LayoutPrefValue, type ShellPrefsDoc, type ShellPrefsTransport,
} from './prefs'
import {
  EMPTY_EDITORS, clearSnapshots, closeSession, minimiseSession, openSession, readSnapshots,
  restoreSession, updateSession, writeSnapshots,
  type EditorIntent, type EditorSession, type EditorSnapshot, type EditorsState,
} from './editorSessions'
import { buildNavTree, findHome, findScreen, pathOf, pathText, type NavTree, type NavTreeNode } from './tree'
import type { RunNavItem } from './types'

export interface UseShellNavOptions {
  nav: RunNavItem[]
  rootParentId?: string
  /** Controlled selection (URL): the obj_name to show, null for none; undefined = internal. */
  selectedObjName?: string | null
  onSelect?: (item: RunNavItem | null, meta?: { auto?: boolean }) => void
  /** Server record for layout / Pinned / Recent. Absent ⇒ local mirror only. */
  prefsTransport?: ShellPrefsTransport
  /** `application.shell`. */
  shellConfig?: AppShellSettings | null
  /** Identify the application and the user for the local mirror + editor snapshots. */
  appKey?: string
  userKey?: string
  /** Open editors on (the Run view). Off ⇒ screens open their editors themselves, as before. */
  editors?: boolean
  /** The host's session expired (it can no longer save). */
  sessionExpired?: boolean
}

export interface ShellEntry { item: RunNavItem; path: RunNavItem[]; pathText: string }
export interface RecentShellEntry extends ShellEntry { at: number }

export interface ShellNavModel {
  nav: RunNavItem[]
  tree: NavTree
  roots: NavTreeNode[]
  current: RunNavItem | null
  missingObjName: string
  open: (item: RunNavItem) => void
  close: () => void
  itemFor: (objName: string) => RunNavItem | null
  pathOf: (item: RunNavItem) => RunNavItem[]
  pathText: (item: RunNavItem) => string
  /** Screens opened and not closed, in the order opened — the tabs, windows
   *  and sheets of the launchpad / desktop / office layouts. Shared, so a
   *  layout switch keeps them. The current screen is always among them. */
  openScreens: RunNavItem[]
  /** Close one open screen (its tab / window); the current one moves to its
   *  neighbour, or to nothing. */
  closeScreen: (item: RunNavItem) => void
  /** Close every open screen. */
  closeAllScreens: () => void
  /** No screen in front, every open one kept open (a desktop's windows all
   *  minimised / behind). */
  blur: () => void
  /** Classic-tree expansion (shared so switching layouts keeps it). */
  expanded: Set<string>
  toggleExpanded: (id: string) => void
  setExpanded: (ids: Iterable<string>, on: boolean) => void
  // Pinned / Recent
  pinned: ShellEntry[]
  isPinned: (item: RunNavItem | null | undefined) => boolean
  togglePin: (item: RunNavItem) => void
  movePin: (item: RunNavItem, delta: number) => void
  recent: RecentShellEntry[]
  clearRecent: () => void
  // Open editors
  editorsEnabled: boolean
  editors: EditorSession[]
  activeEditorId: string | null
  activeEditor: EditorSession | null
  requestEditor: (intent: EditorIntent) => boolean
  restoreEditor: (id: string) => void
  minimiseEditor: (id?: string) => void
  /** Close from a list (×): the session's own Save/Discard rule decides. */
  closeEditor: (id: string) => void
  /** Called by a session instance when its editor really closed. */
  editorClosed: (id: string) => void
  reportEditor: (id: string, patch: Partial<Pick<EditorSession, 'dirty' | 'title' | 'mode' | 'txnId'>>) => void
  snapshotEditor: (id: string, snap: EditorSnapshot) => void
  editorSaved: (id: string) => void
  refreshTokenFor: (objName: string) => number
  closeRequestFor: (id: string) => number
  capNotice: { max: number } | null
  dismissCapNotice: () => void
  /** Ask the layout to show its open-editors list (bumps `editorsListRequest`). */
  openEditorsList: () => void
  /** Layouts watch this: when it changes, reveal their open-editors list. */
  editorsListRequest: number
  dirtyEditors: EditorSession[]
  confirmSignOut: () => boolean
  afterSignOut: () => void
  discardAllEditors: () => void
  sessionExpired: boolean
  // Chrome
  fullScreen: boolean
  setFullScreen: (on: boolean) => void
  layout: LayoutId
  /** The layout actually drawn (phone width ⇒ classic). */
  effectiveLayout: LayoutId
  setLayout: (id: LayoutId) => void
  isPhone: boolean
  config: ResolvedShellConfig
  /** A layout's own small setting (server-saved with the user's prefs), its
   *  shellConfig.json default otherwise. */
  layoutPref: <T extends LayoutPrefValue>(layout: LayoutId, key: string, fallback?: T) => T
  setLayoutPref: (layout: LayoutId, key: string, value: LayoutPrefValue) => void
  /** The user's own desktop picture (an image data URL), when the host stores one. */
  wallpaper: { image: string | null; loaded: boolean; supported: boolean }
  loadWallpaper: () => void
  setWallpaper: (image: string | null) => Promise<void>
  /** `<application>:<user>` — a layout's key for per-device view state. */
  storageScope: string
}

const SAVE_DEBOUNCE_MS = 500

function usePhone(breakpoint: number): boolean {
  const query = `(max-width: ${breakpoint}px)`
  const get = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(query).matches : false)
  const [phone, setPhone] = useState(get)
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined
    const mq = window.matchMedia(query)
    const on = () => setPhone(mq.matches)
    on()
    mq.addEventListener?.('change', on)
    return () => mq.removeEventListener?.('change', on)
  }, [query])
  return phone
}

export function useShellNav(opts: UseShellNavOptions): ShellNavModel {
  const {
    nav, rootParentId, selectedObjName, onSelect, prefsTransport, shellConfig,
    appKey = '', userKey = '', editors: editorsOn = false, sessionExpired = false,
  } = opts
  const controlled = selectedObjName !== undefined
  const config = useMemo(() => resolveShellConfig(shellConfig), [shellConfig])
  const tree = useMemo(() => buildNavTree(nav, rootParentId), [nav, rootParentId])
  const byId = useMemo(() => new Map(nav.map((n) => [n.id, n])), [nav])
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect

  // ── Current screen ──────────────────────────────────────────────────
  const [internalSelected, setInternalSelected] = useState<RunNavItem | null>(null)
  const [missingObjName, setMissingObjName] = useState('')
  const [expanded, setExpandedState] = useState<Set<string>>(new Set())
  const homeSettledRef = useRef(false)

  const expandTo = useCallback((item: RunNavItem) => {
    const ids = pathOf(byId, item).map((p) => p.id)
    if (!ids.length) return
    setExpandedState((prev) => {
      if (ids.every((id) => prev.has(id))) return prev
      const next = new Set(prev)
      ids.forEach((id) => next.add(id))
      return next
    })
  }, [byId])

  // Expand root folders on first data arrival (preserve the user's state after).
  useEffect(() => {
    if (!nav.length) return
    setExpandedState((prev) => (prev.size > 0 ? prev : new Set(nav.filter((n) => !n.parent_id).map((r) => r.id))))
  }, [nav])

  useEffect(() => {
    if (!controlled) return
    const target = (selectedObjName || '').trim()
    if (!target) { setInternalSelected(null); setMissingObjName(''); return }
    const item = findScreen(nav, target)
    setInternalSelected((cur) => (cur && cur.obj_name === target && item && cur.id === item.id ? cur : item))
    // A URL naming a screen this navigation does not contain — a stale bookmark,
    // or one the caller's roles hide — must SAY so.
    setMissingObjName(item || !nav.length ? '' : target)
    if (item) expandTo(item)
  }, [controlled, selectedObjName, nav, expandTo])

  useEffect(() => {
    if (controlled) return
    setInternalSelected((cur) => (cur && !nav.some((n) => n.id === cur.id) ? null : cur))
  }, [controlled, nav])

  const current = internalSelected

  // Open screens (tabs / windows): whatever becomes current joins the list;
  // a screen that leaves the navigation (rights) leaves it too.
  const [openIds, setOpenIds] = useState<string[]>([])
  useEffect(() => {
    if (!current) return
    setOpenIds((ids) => (ids.includes(current.id) ? ids : [...ids, current.id]))
  }, [current])
  useEffect(() => {
    setOpenIds((ids) => {
      const next = ids.filter((id) => byId.has(id))
      return next.length === ids.length ? ids : next
    })
  }, [byId])
  const openScreens = useMemo(
    () => openIds.map((id) => byId.get(id)).filter((x): x is RunNavItem => !!x),
    [openIds, byId],
  )

  // ── Editors state (declared early: navigation minimises) ────────────
  const editorsEnabled = editorsOn
  const [edState, setEdState] = useState<EditorsState>(EMPTY_EDITORS)
  const edStateRef = useRef(edState)
  edStateRef.current = edState
  const snapshotsRef = useRef<Map<string, EditorSnapshot>>(new Map())
  const [refreshTokens, setRefreshTokens] = useState<Record<string, number>>({})
  const [closeRequests, setCloseRequests] = useState<Record<string, number>>({})
  const [capNotice, setCapNotice] = useState<{ max: number } | null>(null)
  const [editorsListRequest, setEditorsListRequest] = useState(0)
  /** After a reload: the session that was on screen, so the URL's own
   *  `?mode=add` re-focuses it rather than opening a second blank add. */
  const resumeAddRef = useRef<{ id: string; objName: string } | null>(null)

  const select = useCallback((item: RunNavItem | null, meta?: { auto?: boolean }) => {
    if (!controlled) setInternalSelected(item)
    if (item) expandTo(item)
    onSelectRef.current?.(item, meta)
  }, [controlled, expandTo])

  const open = useCallback((item: RunNavItem) => {
    if (item.obj_type === '-') return
    homeSettledRef.current = true
    // Leaving the screen an editor is shown on minimises it — never a Save /
    // Discard prompt for moving about.
    setEdState((s) => {
      const act = s.sessions.find((x) => x.id === s.activeId)
      return act && act.objName !== item.obj_name ? minimiseSession(s, act.id) : s
    })
    select(item)
  }, [select])

  const close = useCallback(() => {
    homeSettledRef.current = true
    setEdState((s) => minimiseSession(s))
    if (current) setOpenIds((ids) => ids.filter((id) => id !== current.id))
    select(null)
  }, [select, current])

  const closeScreen = useCallback((item: RunNavItem) => {
    const ids = openIds
    const i = ids.indexOf(item.id)
    const rest = ids.filter((id) => id !== item.id)
    setOpenIds(rest)
    if (current?.id !== item.id) return
    homeSettledRef.current = true
    setEdState((s) => minimiseSession(s))
    const nextId = rest[Math.min(Math.max(0, i - 1), rest.length - 1)]
    const next = nextId ? byId.get(nextId) : undefined
    select(next || null)
  }, [openIds, current, byId, select])
  const closeAllScreens = useCallback(() => {
    setOpenIds([])
    homeSettledRef.current = true
    setEdState((s) => minimiseSession(s))
    select(null)
  }, [select])
  const blur = useCallback(() => {
    homeSettledRef.current = true
    setEdState((s) => minimiseSession(s))
    select(null)
  }, [select])

  // A URL change to another screen (Back, a link) minimises a shown editor too.
  useEffect(() => {
    if (!current) return
    setEdState((s) => {
      const act = s.sessions.find((x) => x.id === s.activeId)
      return act && act.objName !== current.obj_name ? minimiseSession(s, act.id) : s
    })
  }, [current])

  // Smart-page CTAs fire a CANCELABLE 'smartpage:navigate'; claim it when the
  // target is one of OUR screens.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { objType?: string; objName?: string } | undefined
      const objName = (detail?.objName || '').trim()
      if (!objName) return
      const item = nav.find((n) => n.obj_type !== '-' && n.obj_name === objName
        && (!detail?.objType || n.obj_type === detail.objType))
      if (!item) return
      e.preventDefault()
      open(item)
    }
    window.addEventListener('smartpage:navigate', handler)
    return () => window.removeEventListener('smartpage:navigate', handler)
  }, [nav, open])

  // Home on load — once, only when nothing else was asked for.
  useEffect(() => {
    if (homeSettledRef.current || !nav.length) return
    const asked = controlled ? !!(selectedObjName || '').trim() : !!internalSelected
    if (asked) { homeSettledRef.current = true; return }
    const home = findHome(nav, rootParentId)
    if (!home) return
    homeSettledRef.current = true
    select(home, { auto: true })
  }, [nav, rootParentId, controlled, selectedObjName, internalSelected, select])

  const toggleExpanded = useCallback((id: string) => {
    setExpandedState((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])
  const setExpanded = useCallback((ids: Iterable<string>, on: boolean) => {
    setExpandedState((prev) => {
      const next = new Set(prev)
      for (const id of ids) { if (on) next.add(id); else next.delete(id) }
      return next
    })
  }, [])

  // ── Prefs (layout / pinned / recent) ────────────────────────────────
  const caps = useMemo(() => ({ pinned: config.pinnedLimit, recent: config.recentLimit }), [config])
  const [prefs, setPrefs] = useState<ShellPrefsDoc>(() => (
    (appKey && userKey && readMirror(appKey, userKey)) || EMPTY_PREFS))
  const prefsRef = useRef(prefs)
  prefsRef.current = prefs
  const loadedRef = useRef(false)
  const dirtyBeforeLoadRef = useRef(false)
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const transportRef = useRef(prefsTransport)
  transportRef.current = prefsTransport

  const persist = useCallback((doc: ShellPrefsDoc) => {
    if (appKey && userKey) writeMirror(appKey, userKey, doc)
    if (!loadedRef.current) { dirtyBeforeLoadRef.current = true; return }
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => {
      saveTimer.current = null
      const t = transportRef.current
      if (!t) return
      t.save(prefsRef.current).catch((err: unknown) => {
        // The mirror still has it; the next change retries.
        console.warn('[shell] could not save layout / pinned / recent', err)
      })
    }, SAVE_DEBOUNCE_MS)
  }, [appKey, userKey])

  const updatePrefs = useCallback((fn: (p: ShellPrefsDoc) => ShellPrefsDoc) => {
    setPrefs((prev) => {
      const next = { ...fn(prev), updated_at: Date.now() }
      prefsRef.current = next
      persist(next)
      return next
    })
  }, [persist])

  // Re-read the mirror when the identity becomes known (the host may learn the
  // user after the first render).
  useEffect(() => {
    if (!appKey || !userKey) return
    const m = readMirror(appKey, userKey)
    if (m && !loadedRef.current) setPrefs(m)
  }, [appKey, userKey])

  useEffect(() => {
    loadedRef.current = false
    if (!prefsTransport) { loadedRef.current = true; return undefined }
    let cancelled = false
    prefsTransport.load()
      .then((doc) => {
        if (cancelled) return
        loadedRef.current = true
        if (doc && !dirtyBeforeLoadRef.current) {
          const clean = normalisePrefs(doc, caps)
          prefsRef.current = clean
          setPrefs(clean)
          if (appKey && userKey) writeMirror(appKey, userKey, clean)
        } else if (dirtyBeforeLoadRef.current || (!doc && (prefsRef.current.pinned.length
          || prefsRef.current.recent.length || prefsRef.current.layout))) {
          // Changes made before the server answered, or a mirror the server
          // has never seen: the local copy is the newer one.
          dirtyBeforeLoadRef.current = false
          persist(prefsRef.current)
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return
        // A host without the endpoint (an older server) or a refusal: keep the
        // local mirror, never break the shell over preferences.
        loadedRef.current = true
        console.warn('[shell] layout / pinned / recent unavailable from the server', err)
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefsTransport, appKey, userKey])

  useEffect(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current) }, [])

  // Opening a screen — however it was opened — moves it to the top of Recent.
  const lastRecentRef = useRef('')
  useEffect(() => {
    if (!current || current.obj_type === '-') return
    if (lastRecentRef.current === current.obj_name) return
    lastRecentRef.current = current.obj_name
    const name = current.obj_name
    updatePrefs((p) => ({ ...p, recent: pushRecent(p.recent, name, Date.now(), config.recentLimit) }))
  }, [current, updatePrefs, config.recentLimit])

  const itemFor = useCallback((objName: string) => findScreen(nav, objName), [nav])
  const entryOf = useCallback((item: RunNavItem): ShellEntry => {
    const path = pathOf(byId, item)
    return { item, path, pathText: pathText(path) }
  }, [byId])

  // Rights filtering: an entry is shown only while its screen is in `nav`.
  const pinned = useMemo(() => visibleEntries(prefs.pinned, nav, findScreen)
    .map(({ item }) => entryOf(item)),
  [prefs.pinned, nav, entryOf])
  const recent = useMemo(() => visibleEntries(prefs.recent, nav, findScreen)
    .map(({ entry, item }): RecentShellEntry => ({ ...entryOf(item), at: entry.at }))
    .slice(0, config.recentLimit),
  [prefs.recent, nav, entryOf, config.recentLimit])

  const isPinned = useCallback((item: RunNavItem | null | undefined) => !!item
    && prefs.pinned.some((p) => p.obj_name === item.obj_name), [prefs.pinned])
  const togglePin = useCallback((item: RunNavItem) => {
    if (item.obj_type === '-') return
    updatePrefs((p) => ({ ...p, pinned: togglePinned(p.pinned, item.obj_name, config.pinnedLimit) }))
  }, [updatePrefs, config.pinnedLimit])
  const movePin = useCallback((item: RunNavItem, delta: number) => {
    updatePrefs((p) => ({ ...p, pinned: movePinned(p.pinned, item.obj_name, delta) }))
  }, [updatePrefs])
  const clearRecent = useCallback(() => {
    lastRecentRef.current = ''
    updatePrefs((p) => ({ ...p, recent: [] }))
  }, [updatePrefs])

  // ── Layout ──────────────────────────────────────────────────────────
  const isPhone = usePhone(config.phoneBreakpoint)
  // A test run pins the layout (the UI acceptance runner sets
  // `window.__TWASTA_SHELL_LAYOUT__ = 'classic'`, whose menus its cases drive)
  // whatever the persona chose for themselves.
  const forced = typeof window !== 'undefined'
    ? (window as unknown as { __TWASTA_SHELL_LAYOUT__?: unknown }).__TWASTA_SHELL_LAYOUT__ : undefined
  const layout = isLayoutId(forced) ? forced : resolveLayout(prefs.layout, config)
  const setLayout = useCallback((id: LayoutId) => {
    updatePrefs((p) => ({ ...p, layout: id }))
  }, [updatePrefs])

  // ── Per-layout settings + the desktop picture ───────────────────────
  const layoutPref = useCallback(<T extends LayoutPrefValue>(id: LayoutId, key: string, fallback?: T): T => {
    const own = prefs.layoutPrefs?.[id]?.[key]
    if (own !== undefined) return own as T
    const def = layoutDefaults(id)[key]
    return (def !== undefined ? def : fallback) as T
  }, [prefs.layoutPrefs])
  const setLayoutPref = useCallback((id: LayoutId, key: string, value: LayoutPrefValue) => {
    updatePrefs((p) => ({
      ...p, layoutPrefs: { ...(p.layoutPrefs || {}), [id]: { ...(p.layoutPrefs?.[id] || {}), [key]: value } },
    }))
  }, [updatePrefs])
  const [wallpaper, setWallpaperState] = useState<{ image: string | null; loaded: boolean }>({ image: null, loaded: false })
  const wallpaperAsked = useRef(false)
  useEffect(() => { wallpaperAsked.current = false; setWallpaperState({ image: null, loaded: false }) }, [prefsTransport])
  const loadWallpaper = useCallback(() => {
    const t = transportRef.current
    if (wallpaperAsked.current || !t?.loadWallpaper) return
    wallpaperAsked.current = true
    t.loadWallpaper()
      .then((image) => setWallpaperState({ image: image || null, loaded: true }))
      .catch((err: unknown) => {
        console.warn('[shell] desktop picture unavailable', err)
        setWallpaperState({ image: null, loaded: true })
      })
  }, [])
  const setWallpaper = useCallback(async (image: string | null) => {
    const t = transportRef.current
    if (!t?.saveWallpaper) throw new Error('This server cannot keep a desktop picture.')
    await t.saveWallpaper(image)
    setWallpaperState({ image, loaded: true })
  }, [])

  // ── Full screen ─────────────────────────────────────────────────────
  const [fullScreen, setFullScreenState] = useState(false)
  const setFullScreen = useCallback((on: boolean) => setFullScreenState(on), [])
  useEffect(() => { setFullScreenState(false) }, [current?.obj_name])
  useEffect(() => {
    if (!fullScreen) return undefined
    const onKey = (e: KeyboardEvent) => {
      // A dialog over the screen (an editor, a lookup) handles its own Esc first.
      if (e.key !== 'Escape' || e.defaultPrevented) return
      if (document.querySelector('[data-tw-dialog]')) return
      setFullScreenState(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [fullScreen])

  // ── Open editors ────────────────────────────────────────────────────
  const max = config.editors.maxSessions

  // Rebuild the list from this tab's snapshots after a reload — minimised.
  const restoredRef = useRef('')
  useEffect(() => {
    if (!editorsEnabled || !appKey || !userKey) return
    const key = `${appKey}|${userKey}`
    if (restoredRef.current === key) return
    restoredRef.current = key
    const rebuilt = readSnapshots(appKey, userKey, {
      now: Date.now(), maxAgeHours: config.editors.snapshotMaxAgeHours, max,
    })
    if (!rebuilt.length) return
    for (const s of rebuilt) if (s.snapshot) snapshotsRef.current.set(s.id, s.snapshot)
    try {
      const raw = sessionStorage.getItem(`twasta:shell:editors-active:${appKey}:${userKey}`)
      const act = raw ? rebuilt.find((s) => s.id === raw) : undefined
      if (act && act.mode === 'add') resumeAddRef.current = { id: act.id, objName: act.objName }
    } catch { /* no hint */ }
    setEdState((s) => ({
      sessions: [...s.sessions, ...rebuilt.filter((r) => !s.sessions.some((x) => x.id === r.id))].slice(0, max),
      activeId: s.activeId,
    }))
  }, [editorsEnabled, appKey, userKey, config.editors.snapshotMaxAgeHours, max])

  // Persist the session list (+ snapshots) to this tab's storage, debounced.
  const snapTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const flushSnapshots = useCallback(() => {
    if (!editorsEnabled || !appKey || !userKey) return
    const list = edStateRef.current.sessions.map((s) => ({ ...s, snapshot: snapshotsRef.current.get(s.id) || s.snapshot }))
    writeSnapshots(appKey, userKey, list)
    try {
      const key = `twasta:shell:editors-active:${appKey}:${userKey}`
      if (edStateRef.current.activeId) sessionStorage.setItem(key, edStateRef.current.activeId)
      else sessionStorage.removeItem(key)
    } catch { /* hint only */ }
  }, [editorsEnabled, appKey, userKey])
  const scheduleSnapshots = useCallback(() => {
    if (snapTimer.current) clearTimeout(snapTimer.current)
    snapTimer.current = setTimeout(() => { snapTimer.current = null; flushSnapshots() },
      config.editors.snapshotDebounceMs)
  }, [flushSnapshots, config.editors.snapshotDebounceMs])
  useEffect(() => { scheduleSnapshots() }, [edState, scheduleSnapshots])
  useEffect(() => () => { if (snapTimer.current) clearTimeout(snapTimer.current) }, [])

  const screenTitleOf = useCallback((objName: string) => itemFor(objName)?.title || objName, [itemFor])

  const requestEditor = useCallback((intent: EditorIntent): boolean => {
    if (!editorsEnabled) return false
    const resume = resumeAddRef.current
    if (intent.mode === 'add' && resume && resume.objName === intent.objName && !intent.seed && !intent.open) {
      resumeAddRef.current = null
      if (edStateRef.current.sessions.some((s) => s.id === resume.id)) {
        setEdState((s) => restoreSession(s, resume.id))
        return true
      }
    }
    const title = intent.mode === 'add' ? `New ${screenTitleOf(intent.objName)}` : ''
    const { state, result } = openSession(edStateRef.current, intent, { max, title, now: Date.now() })
    if (result.kind === 'cap') {
      setCapNotice({ max: result.max })
      return true
    }
    edStateRef.current = state
    setEdState(state)
    return true
  }, [editorsEnabled, max, screenTitleOf])

  const restoreEditor = useCallback((id: string) => {
    const s = edStateRef.current.sessions.find((x) => x.id === id)
    if (!s) return
    const item = itemFor(s.objName)
    if (item && current?.obj_name !== item.obj_name) {
      homeSettledRef.current = true
      select(item)
    }
    setEdState((st) => restoreSession(st, id))
  }, [itemFor, current, select])

  const minimiseEditor = useCallback((id?: string) => {
    setEdState((s) => minimiseSession(s, id))
    // Minimise is the moment the snapshot must be on disk (the user may now
    // reload): flush without waiting for the debounce.
    setTimeout(flushSnapshots, 0)
  }, [flushSnapshots])

  const editorClosed = useCallback((id: string) => {
    snapshotsRef.current.delete(id)
    setEdState((s) => closeSession(s, id))
  }, [])

  const closeEditor = useCallback((id: string) => {
    const s = edStateRef.current.sessions.find((x) => x.id === id)
    if (!s) return
    if (!s.mounted) {
      // Rebuilt from a snapshot and never restored: the form is not mounted to
      // ask, so the shell asks the same question the form would.
      if (s.dirty && !window.confirm(`Discard the unsaved changes to “${s.title || 'this record'}”?`)) return
      editorClosed(id)
      return
    }
    // A mounted form applies its own Save / Discard rule.
    setCloseRequests((m) => ({ ...m, [id]: (m[id] || 0) + 1 }))
  }, [editorClosed])

  const reportEditor = useCallback((id: string, patch: Partial<Pick<EditorSession, 'dirty' | 'title' | 'mode' | 'txnId'>>) => {
    setEdState((s) => updateSession(s, id, patch))
  }, [])
  const snapshotEditor = useCallback((id: string, snap: EditorSnapshot) => {
    snapshotsRef.current.set(id, snap)
    scheduleSnapshots()
  }, [scheduleSnapshots])
  const editorSaved = useCallback((id: string) => {
    const s = edStateRef.current.sessions.find((x) => x.id === id)
    if (!s) return
    setRefreshTokens((m) => ({ ...m, [s.objName]: (m[s.objName] || 0) + 1 }))
  }, [])

  const refreshTokenFor = useCallback((objName: string) => refreshTokens[objName] || 0, [refreshTokens])
  const closeRequestFor = useCallback((id: string) => closeRequests[id] || 0, [closeRequests])

  const dirtyEditors = useMemo(() => edState.sessions.filter((s) => s.dirty), [edState.sessions])

  // Leaving the page with unsaved work: the browser's own warning.
  useEffect(() => {
    if (!dirtyEditors.length) return undefined
    const onBefore = (e: BeforeUnloadEvent) => {
      flushSnapshots()
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBefore)
    return () => window.removeEventListener('beforeunload', onBefore)
  }, [dirtyEditors.length, flushSnapshots])

  const discardAllEditors = useCallback(() => {
    snapshotsRef.current.clear()
    if (appKey && userKey) clearSnapshots(appKey, userKey)
    setEdState(EMPTY_EDITORS)
  }, [appKey, userKey])

  const confirmSignOut = useCallback((): boolean => {
    const dirty = edStateRef.current.sessions.filter((s) => s.dirty)
    if (!dirty.length) return true
    const list = dirty.map((s) => `• ${s.title || 'Untitled'} (${screenTitleOf(s.objName)})`).join('\n')
    return window.confirm(`Signing out discards ${dirty.length === 1 ? 'this unsaved editor' : 'these unsaved editors'}:\n\n${list}\n\nSign out anyway?`)
  }, [screenTitleOf])

  const activeEditor = edState.sessions.find((s) => s.id === edState.activeId) || null

  return {
    nav, tree, roots: tree.roots, current, missingObjName, open, close, itemFor,
    openScreens, closeScreen, closeAllScreens, blur,
    pathOf: (item) => pathOf(byId, item),
    pathText: (item) => pathText(pathOf(byId, item)),
    expanded, toggleExpanded, setExpanded,
    pinned, isPinned, togglePin, movePin, recent, clearRecent,
    editorsEnabled, editors: edState.sessions, activeEditorId: edState.activeId, activeEditor,
    requestEditor, restoreEditor, minimiseEditor, closeEditor, editorClosed, reportEditor,
    snapshotEditor, editorSaved, refreshTokenFor, closeRequestFor,
    capNotice, dismissCapNotice: () => setCapNotice(null),
    openEditorsList: () => setEditorsListRequest((n) => n + 1), editorsListRequest,
    dirtyEditors, confirmSignOut, afterSignOut: discardAllEditors, discardAllEditors, sessionExpired,
    fullScreen, setFullScreen,
    layout, effectiveLayout: isPhone ? 'classic' : layout, setLayout, isPhone, config,
    layoutPref, setLayoutPref,
    wallpaper: { ...wallpaper, supported: !!prefsTransport?.saveWallpaper },
    loadWallpaper, setWallpaper,
    storageScope: `${appKey}:${userKey}`,
  }
}

export type { EditorSession, EditorIntent } from './editorSessions'
